import {
    Box, Card, Dialog, DialogContent, AppBar, Toolbar,
    Grid, CardContent, Typography, Chip, IconButton,
    Accordion, AccordionSummary, AccordionDetails, Popover
} from "@material-ui/core";
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { useEffect, useState } from "react";

import CloseIcon from '@material-ui/icons/Close';

import { useTranslation } from "react-i18next";
import useStyles from './styles';
import { debugLog } from '../../../common/debugUtilities';

const PALETTE = {
    bg: '#f5f6fa',
    cardBg: '#ffffff',
    headerGradient: 'linear-gradient(135deg, #0288d1 0%, #283593 100%)',
    h2: '#0288d1',
    h2Underline: '#e8eaf6',
    text: '#2c3e50',
    textSecondary: '#546e7a',
    textMuted: '#78909c',
    statBg: '#f5f6fa',
    typical: '#43a047',
    typicalBg: '#e8f5e9',
    atypical: '#e65100',
    atypicalBg: '#fff3e0',
    info: '#1565c0',
    infoBg: '#e3f2fd',
    cardShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

const cardSx = {
    backgroundColor: PALETTE.cardBg,
    borderRadius: '10px',
    boxShadow: PALETTE.cardShadow,
    marginBottom: '20px',
};

// Z-score scale anchors for the atypicality bar.
// Anchors map z-values to %-positions across the bar.
// -2.0 → 0%, -0.25 → typical/borderline boundary, +0.5 → borderline/typical-clear, +2.0 → 100%.
const Z_MIN = -2.0;
const Z_MAX = 2.0;

const zToPct = (z) => {
    if (z == null || isNaN(z)) return 50;
    const clamped = Math.max(Z_MIN, Math.min(Z_MAX, z));
    return ((clamped - Z_MIN) / (Z_MAX - Z_MIN)) * 100;
};

function InfoPopover({ children }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    return (
        <>
            <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}
                style={{ position: 'absolute', top: 4, right: 4, padding: 4, color: PALETTE.info }}
                aria-label="more info"
            >
                <InfoOutlinedIcon style={{ fontSize: 18 }} />
            </IconButton>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                PaperProps={{ style: { maxWidth: 360, padding: 16, borderRadius: 8 } }}
            >
                {children}
            </Popover>
        </>
    );
}

const sectionTitleSx = {
    fontSize: '17px',
    fontWeight: 600,
    color: PALETTE.h2,
    borderBottom: `2px solid ${PALETTE.h2Underline}`,
    paddingBottom: '8px',
    marginBottom: '16px',
};

export default function ExplainableReport (props) {

    const {t, i18n} = useTranslation();
    const {isOpen, assessment, dialogComplete} = props;

    const getOrdinal = (n) => {
        const s = ["th", "st", "nd", "rd"];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    const getPercentileLabel = (p) => {
        if (p == null || isNaN(p)) return null;
        if (p <= 5 || p >= 95) return 'Extreme value vs typical cohort';
        if (p <= 15 || p >= 85) return 'Outside typical range';
        if (p <= 35 || p >= 65) return 'Edge of typical range';
        return 'Near typical center';
    };

    const [zScore, setZScore] = useState(0);
    const [percentile, setPercentile] = useState(50);
    const [pValue, setPValue] = useState(0.5);
    const [classification, setClassification] = useState('typical');
    const [threshold, setThreshold] = useState(-0.2463);
    const [clinicalSummary, setClinicalSummary] = useState('');
    const [featureContributions, setFeatureContributions] = useState([]);
    const [videoQuality, setVideoQuality] = useState(0);
    const [infantAgeWeeks, setInfantAgeWeeks] = useState(null);
    const [modelInfo, setModelInfo] = useState(null);

    const classes = useStyles();

    useEffect( ()=>{
        debugLog('Explainable report - current asmt:', assessment?.notes);
        setZScore(assessment?.notes?.z ?? 0);
        setPercentile(assessment?.notes?.percentile ?? 50);
        setPValue(assessment?.notes?.p ?? 0.5);
        setClassification(assessment?.notes?.classification ?? 'typical');
        setThreshold(assessment?.notes?.threshold ?? -0.2463);
        setClinicalSummary(assessment?.notes?.clinical_summary ?? '');
        setFeatureContributions(assessment?.notes?.feature_contributions ?? []);
        setVideoQuality(assessment?.notes?.quality_index ?? 0);
        setInfantAgeWeeks(assessment?.notes?.age ?? assessment?.notes?.age_weeks ?? null);
        setModelInfo(assessment?.notes?.model_info ?? null);
    },[assessment]);

    const isAtRisk = classification === 'at_risk';
    const classificationLabel = isAtRisk
        ? 'BELOW THRESHOLD - Further evaluation recommended'
        : 'ABOVE THRESHOLD - Typical development likely';

    return (
        <Dialog open={isOpen} fullScreen className={classes.paper}>
            <DialogContent style={{ backgroundColor: PALETTE.bg, padding: 0 }}>

                <AppBar
                    className={classes.appbar}
                    style={{
                        backgroundColor: '#ffffff',
                        color: PALETTE.h2,
                        boxShadow: '0 1px 0 rgba(0,0,0,0.06)',
                    }}
                    position="sticky"
                >
                    <Toolbar>
                        <IconButton
                            edge="start"
                            aria-label="close"
                            onClick={dialogComplete}
                            style={{ color: PALETTE.h2 }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <Typography variant="h6" component="div" style={{ fontWeight: 600, marginLeft: 8 }}>
                            Neuromotor Screening Report
                        </Typography>
                    </Toolbar>
                </AppBar>

                <Box style={{ maxWidth: 1100, margin: '0 auto', padding: '24px' }}>

                    {/* Hero / gradient summary header */}
                    <Box
                        style={{
                            background: PALETTE.headerGradient,
                            color: 'white',
                            padding: '32px 40px',
                            borderRadius: '12px',
                            marginBottom: '24px',
                            boxShadow: PALETTE.cardShadow,
                        }}
                    >
                        <Typography variant="h5" style={{ fontWeight: 600 }}>
                            Neuromotor Screening Result
                        </Typography>
                        <Typography variant="body2" style={{ opacity: 0.85, marginTop: 4 }}>
                            Automated movement analysis &middot; based on infant pose features
                        </Typography>
                        {infantAgeWeeks !== null && (
                            <Typography variant="body2" style={{ opacity: 0.7, marginTop: 12 }}>
                                Infant age at video: <strong>{infantAgeWeeks} weeks</strong>
                            </Typography>
                        )}
                    </Box>

                    {/* Explainer accordions */}
                    <Accordion
                        style={{
                            borderRadius: '10px',
                            boxShadow: PALETTE.cardShadow,
                            marginBottom: '12px',
                            backgroundColor: PALETTE.infoBg,
                            '&:before': { display: 'none' },
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon style={{ color: PALETTE.h2 }} />}
                            style={{ padding: '0 24px' }}
                        >
                            <Typography style={{ fontWeight: 600, color: PALETTE.h2, fontSize: 15 }}>
                                How Cribsy works
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails style={{ padding: '0 24px 20px', display: 'block' }}>
                            <Typography variant="body2" style={{ color: PALETTE.textSecondary, marginBottom: 12 }}>
                                Cribsy screens for atypical neuromotor development from a short video of a baby moving freely.
                                The pipeline runs in three stages:
                            </Typography>

                            <Box style={{ paddingLeft: 16, color: PALETTE.textSecondary }}>
                                <Typography variant="body2" style={{ marginBottom: 8 }}>
                                    <strong style={{ color: PALETTE.text }}>1. Pose estimation.</strong> Computer-vision models track 25+ body keypoints
                                    (hips, knees, shoulders, hands, head) frame by frame, producing a time series of 2D positions.
                                </Typography>
                                <Typography variant="body2" style={{ marginBottom: 8 }}>
                                    <strong style={{ color: PALETTE.text }}>2. Movement features.</strong> The pose track is summarized into 19 quantitative
                                    movement features &mdash; symmetry, head-turn distribution, range and smoothness of limb motion, postural variability,
                                    and so on. Each feature captures a clinically meaningful aspect of spontaneous movement.
                                </Typography>
                                <Typography variant="body2" style={{ marginBottom: 8 }}>
                                    <strong style={{ color: PALETTE.text }}>3. Age-adjusted scoring.</strong> The 19 features plus the infant&apos;s age
                                    are passed through a model trained on a reference cohort of typical and at-risk infants. The model outputs a single
                                    z-score, with positive = typical and negative = atypical relative to that reference distribution.
                                </Typography>
                                <Typography variant="body2">
                                    <strong style={{ color: PALETTE.text }}>Explainability.</strong> The Movement Feature Contributions table below
                                    decomposes the score: it shows which features pushed the result toward typical vs. at-risk and by how much.
                                    The 19 movement features always sum to 100% of the movement signal.
                                </Typography>
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    {/* SUMMARY card */}
                    <Card style={cardSx}>
                        <CardContent>
                            <Typography style={sectionTitleSx}>SUMMARY</Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box style={{ background: PALETTE.statBg, borderRadius: 8, padding: '12px 16px', textAlign: 'center', position: 'relative' }}>
                                        <InfoPopover>
                                            <Typography variant="subtitle2" style={{ color: PALETTE.text, marginBottom: 6 }}>
                                                Z-score &mdash; how far from the typical mean
                                            </Typography>
                                            <Typography variant="body2" style={{ color: PALETTE.textSecondary, marginBottom: 8 }}>
                                                Number of standard deviations from the mean of the typical-development reference cohort (n=127).
                                                Positive = more typical, negative = more atypical. The atypicality bar below shows where this child falls,
                                                with a blue dot at the exact z-position.
                                            </Typography>
                                            <Typography variant="body2" style={{ color: PALETTE.textSecondary }}>
                                                The classification banner combines the z-score with a calibrated decision threshold (currently {threshold.toFixed(4)}).
                                                The model is tuned for high sensitivity (92.1%) over specificity (78.0%) so fewer at-risk infants are missed &mdash;
                                                at the cost of some false positives needing clinical follow-up.
                                            </Typography>
                                        </InfoPopover>
                                        <Typography variant="caption" style={{ color: PALETTE.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Z-score
                                        </Typography>
                                        <Typography variant="h4" style={{ fontWeight: 700, color: PALETTE.h2 }}>
                                            {zScore.toFixed(2)}
                                        </Typography>
                                        <Typography variant="caption" style={{ color: PALETTE.textMuted }}>
                                            threshold {threshold.toFixed(4)}
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <Box style={{ background: PALETTE.statBg, borderRadius: 8, padding: '12px 16px', textAlign: 'center', position: 'relative' }}>
                                        <InfoPopover>
                                            <Typography variant="subtitle2" style={{ color: PALETTE.text, marginBottom: 6 }}>
                                                Percentile &mdash; rank in the typical population
                                            </Typography>
                                            <Typography variant="body2" style={{ color: PALETTE.textSecondary, marginBottom: 6 }}>
                                                Cumulative area under the normal curve up to this z-score:
                                                <code style={{ background: PALETTE.statBg, padding: '1px 5px', borderRadius: 3, marginLeft: 4 }}>percentile = Φ(z) × 100</code>.
                                            </Typography>
                                            <Box component="ul" style={{ margin: 0, paddingLeft: 20, color: PALETTE.textSecondary, marginBottom: 8 }}>
                                                <li>z = 0 &rarr; 50th percentile (right at the mean)</li>
                                                <li>z = -1 &rarr; ~16th percentile</li>
                                                <li>z = +1 &rarr; ~84th percentile</li>
                                                <li>z = -2 &rarr; ~2nd percentile</li>
                                            </Box>
                                            <Typography variant="caption" style={{ color: PALETTE.textMuted }}>
                                                Numerically, percentile and one-tailed p-value carry the same information
                                                (percentile = p × 100). They&apos;re shown side by side because they speak to different
                                                audiences: percentile is the intuitive growth-chart framing.
                                            </Typography>
                                        </InfoPopover>
                                        <Typography variant="caption" style={{ color: PALETTE.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Percentile
                                        </Typography>
                                        <Typography variant="h4" style={{ fontWeight: 700, color: PALETTE.h2 }}>
                                            {getOrdinal(percentile)}
                                        </Typography>
                                        <Typography variant="caption" style={{ color: PALETTE.textMuted }}>
                                            reference population n=127
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <Box style={{ background: PALETTE.statBg, borderRadius: 8, padding: '12px 16px', textAlign: 'center', position: 'relative' }}>
                                        <InfoPopover>
                                            <Typography variant="subtitle2" style={{ color: PALETTE.text, marginBottom: 6 }}>
                                                P-value &mdash; how surprising the score would be if the child is typical
                                            </Typography>
                                            <Typography variant="body2" style={{ color: PALETTE.textSecondary, marginBottom: 6 }}>
                                                One-tailed test against the null hypothesis that the child belongs to the typical population:
                                                <code style={{ background: PALETTE.statBg, padding: '1px 5px', borderRadius: 3, marginLeft: 4 }}>p = Φ(z)</code> (lower tail).
                                                A small p means a score this low (or lower) would be unlikely under the null &mdash; the screen flags concern.
                                            </Typography>
                                            <Box component="ul" style={{ margin: 0, paddingLeft: 20, color: PALETTE.textSecondary }}>
                                                <li>z = -1.65 &rarr; p ≈ 0.05 (the classic significance threshold)</li>
                                                <li>z = -2 &rarr; p ≈ 0.023</li>
                                            </Box>
                                        </InfoPopover>
                                        <Typography variant="caption" style={{ color: PALETTE.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            P-value
                                        </Typography>
                                        <Typography variant="h4" style={{ fontWeight: 700, color: PALETTE.h2 }}>
                                            {pValue.toFixed(3)}
                                        </Typography>
                                        <Typography variant="caption" style={{ color: PALETTE.textMuted }}>
                                            one-tailed
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <Box style={{ background: PALETTE.statBg, borderRadius: 8, padding: '12px 16px', textAlign: 'center', position: 'relative' }}>
                                        <InfoPopover>
                                            <Typography variant="subtitle2" style={{ color: PALETTE.text, marginBottom: 6 }}>
                                                Video Quality &mdash; how usable the recording was
                                            </Typography>
                                            <Typography variant="body2" style={{ color: PALETTE.textSecondary }}>
                                                Composite score (0&ndash;100%) reflecting how reliably the pose-estimation pipeline could
                                                track the infant&apos;s body keypoints throughout the video. Lighting, occlusion, framing,
                                                and motion blur all affect this. Lower values mean the downstream movement features are
                                                derived from fewer or noisier frames, which can reduce confidence in the z-score.
                                            </Typography>
                                        </InfoPopover>
                                        <Typography variant="caption" style={{ color: PALETTE.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Video Quality
                                        </Typography>
                                        <Typography variant="h4" style={{ fontWeight: 700, color: PALETTE.h2 }}>
                                            {(videoQuality * 100).toFixed(1)}%
                                        </Typography>
                                        <Typography variant="caption" style={{ color: PALETTE.textMuted }}>
                                            &gt;70% acceptable
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>

                            {/* Atypicality bar with z-position marker */}
                            <Box style={{ marginTop: 24 }}>
                                <Typography variant="caption" style={{ color: PALETTE.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 8, textAlign: 'center' }}>
                                    Where this z-score falls on the screening scale
                                </Typography>

                                <Box style={{ position: 'relative', maxWidth: 500, margin: '0 auto', paddingBottom: 4 }}>
                                    {/* Tri-color band */}
                                    <Box style={{ display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden' }}>
                                        <Box style={{ flex: 1, background: '#e53935' }} title="Atypical (z < -0.25)" />
                                        <Box style={{ flex: 1, background: '#fff176' }} title="Borderline (-0.25 ≤ z < 0.5)" />
                                        <Box style={{ flex: 2, background: '#a5d6a7' }} title="Typical (z ≥ 0.5)" />
                                    </Box>

                                    {/* Z-score marker dot */}
                                    <Box
                                        style={{
                                            position: 'absolute',
                                            left: `${zToPct(zScore)}%`,
                                            top: -3,
                                            width: 18,
                                            height: 18,
                                            borderRadius: '50%',
                                            background: '#0288d1',
                                            border: '3px solid white',
                                            boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
                                            transform: 'translateX(-50%)',
                                        }}
                                        title={`Z = ${zScore.toFixed(2)}`}
                                    />

                                    {/* Scale labels */}
                                    <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: PALETTE.textMuted, marginTop: 10 }}>
                                        <span>{Z_MIN.toFixed(1)}</span>
                                        <span>-0.25</span>
                                        <span>0.5</span>
                                        <span>{Z_MAX.toFixed(1)}</span>
                                    </Box>

                                    {/* Tier labels under each segment */}
                                    <Box style={{ display: 'flex', fontSize: 11, color: PALETTE.textSecondary, marginTop: 4 }}>
                                        <Box style={{ flex: 1, textAlign: 'center', color: PALETTE.atypical, fontWeight: 600 }}>Atypical</Box>
                                        <Box style={{ flex: 1, textAlign: 'center', color: '#f9a825', fontWeight: 600 }}>Borderline</Box>
                                        <Box style={{ flex: 2, textAlign: 'center', color: PALETTE.typical, fontWeight: 600 }}>Typical</Box>
                                    </Box>
                                </Box>
                            </Box>

                            <Box
                                style={{
                                    marginTop: 20,
                                    padding: '12px 16px',
                                    borderRadius: 8,
                                    backgroundColor: isAtRisk ? PALETTE.atypicalBg : PALETTE.typicalBg,
                                    borderLeft: `4px solid ${isAtRisk ? PALETTE.atypical : PALETTE.typical}`,
                                }}
                            >
                                <Typography variant="body1" style={{ fontWeight: 600, color: isAtRisk ? PALETTE.atypical : PALETTE.typical }}>
                                    {classificationLabel}
                                </Typography>
                                {clinicalSummary && (
                                    <Typography variant="body2" style={{ color: PALETTE.textSecondary, marginTop: 6 }}>
                                        <strong style={{ color: PALETTE.text }}>Clinical Summary: </strong>
                                        {clinicalSummary}
                                    </Typography>
                                )}
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Feature contributions */}
                    {featureContributions.length > 0 && (
                        <Card style={cardSx}>
                            <CardContent>
                                <Typography style={sectionTitleSx}>MOVEMENT FEATURE CONTRIBUTIONS</Typography>

                                {infantAgeWeeks !== null && (
                                    <Box style={{ marginBottom: 12, padding: 12, backgroundColor: PALETTE.infoBg, borderRadius: 6 }}>
                                        <Typography variant="body2" style={{ color: PALETTE.info }}>
                                            <strong>Age-adjusted analysis:</strong> Infant age ({infantAgeWeeks} weeks) was accounted for in the model.
                                            The contributions below reflect movement features only. Each feature can push the score toward
                                            <strong style={{ color: PALETTE.typical }}> typicality (green)</strong> or
                                            <strong style={{ color: PALETTE.atypical }}> at-risk (orange)</strong>.
                                        </Typography>
                                    </Box>
                                )}

                                <Grid container spacing={2}>
                                    {featureContributions.filter(f => f.feature !== 'age_in_weeks').slice(0, 5).map((feature, index) => {
                                        const atRisk = feature.direction === 'at_risk';
                                        const accent = atRisk ? PALETTE.atypical : PALETTE.typical;
                                        const accentBg = atRisk ? PALETTE.atypicalBg : PALETTE.typicalBg;
                                        const pctValue = feature.contribution_pct;
                                        return (
                                            <Grid item xs={12} sm={6} md={4} key={index}>
                                                <Box
                                                    style={{
                                                        border: `2px solid ${accent}`,
                                                        backgroundColor: accentBg,
                                                        borderRadius: 10,
                                                        padding: 14,
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                    }}
                                                >
                                                    {/* Rank + direction badge */}
                                                    <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                                        <Box
                                                            style={{
                                                                width: 24,
                                                                height: 24,
                                                                borderRadius: '50%',
                                                                backgroundColor: '#fff',
                                                                color: accent,
                                                                border: `2px solid ${accent}`,
                                                                fontSize: 12,
                                                                fontWeight: 700,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                            }}
                                                        >
                                                            {index + 1}
                                                        </Box>
                                                        <Chip
                                                            label={atRisk ? 'At Risk' : 'Typical'}
                                                            size="small"
                                                            style={{ backgroundColor: accent, color: '#fff', fontWeight: 600, height: 22 }}
                                                        />
                                                    </Box>

                                                    {/* Feature description (heading) + technical name (subheading) */}
                                                    {feature.description ? (
                                                        <>
                                                            <Typography variant="body2" style={{ color: PALETTE.text, marginBottom: 2, lineHeight: 1.3 }}>
                                                                {feature.description}
                                                            </Typography>
                                                            <Typography variant="caption" style={{ color: PALETTE.textSecondary, display: 'block', marginBottom: 10, fontFamily: 'monospace' }}>
                                                                {feature.feature}
                                                            </Typography>
                                                        </>
                                                    ) : (
                                                        <Typography variant="body2" style={{ fontWeight: 700, color: PALETTE.text, marginBottom: 10 }}>
                                                            {feature.feature}
                                                        </Typography>
                                                    )}

                                                    {/* Body region chip */}
                                                    {feature.body_region && (
                                                        <Box style={{ marginBottom: 10 }}>
                                                            <Chip
                                                                label={feature.body_region}
                                                                size="small"
                                                                variant="outlined"
                                                                style={{ borderColor: accent, color: accent, backgroundColor: '#fff' }}
                                                            />
                                                        </Box>
                                                    )}

                                                    {/* Contribution bar — pinned to bottom */}
                                                    <Box style={{ marginTop: 'auto' }}>
                                                        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                                                            <Typography variant="caption" style={{ color: PALETTE.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                                Contribution
                                                            </Typography>
                                                            <Typography variant="h6" style={{ fontWeight: 700, color: accent, lineHeight: 1 }}>
                                                                {pctValue !== undefined ? pctValue + '%' : 'N/A'}
                                                            </Typography>
                                                        </Box>
                                                        <Box style={{ height: 8, backgroundColor: '#fff', borderRadius: 4, overflow: 'hidden', border: '1px solid #eceff1' }}>
                                                            <Box style={{
                                                                width: `${Math.min(pctValue || 0, 100)}%`,
                                                                height: '100%',
                                                                backgroundColor: accent,
                                                                borderRadius: 4,
                                                            }} />
                                                        </Box>
                                                        {getPercentileLabel(feature.percentile) && (
                                                            <Typography variant="caption" style={{ color: PALETTE.textSecondary, display: 'block', marginTop: 6 }}>
                                                                {getPercentileLabel(feature.percentile)}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        );
                                    })}
                                </Grid>

                                <Box style={{ marginTop: 16, padding: 12, backgroundColor: PALETTE.statBg, borderRadius: 6 }}>
                                    <Typography variant="caption" style={{ color: PALETTE.textSecondary }}>
                                        <strong>Contribution %:</strong> Each movement feature&apos;s share of the total movement signal.
                                        The 19 movement features sum to 100%. Top 5 shown.
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {/* Model context */}
                    <Card style={cardSx}>
                        <CardContent>
                            <Typography style={sectionTitleSx}>MODEL CONTEXT</Typography>

                            {modelInfo && (
                                <Box style={{ marginBottom: 16, padding: 12, backgroundColor: PALETTE.statBg, borderRadius: 6 }}>
                                    <Typography variant="body2" style={{ color: PALETTE.textSecondary }}>
                                        <strong style={{ color: PALETTE.text }}>Model:</strong> {modelInfo.name}
                                        &nbsp;&middot;&nbsp; <strong style={{ color: PALETTE.text }}>Version:</strong> {modelInfo.version}
                                        &nbsp;&middot;&nbsp; <strong style={{ color: PALETTE.text }}>Features:</strong> {modelInfo.n_features}
                                        &nbsp;&middot;&nbsp; <strong style={{ color: PALETTE.text }}>Reference:</strong> n={modelInfo.reference_subjects}
                                    </Typography>
                                </Box>
                            )}

                            <Grid container spacing={2}>
                                {[
                                    { label: 'Sensitivity', value: '92.1%' },
                                    { label: 'Specificity', value: '78.0%' },
                                    { label: 'PPV (sample)', value: '74.6%' },
                                    { label: 'NPV (sample)', value: '93.4%' },
                                    { label: 'LR+', value: '4.2', caption: 'Se / (1-Sp)' },
                                    { label: 'LR-', value: '0.10', caption: '(1-Se) / Sp' },
                                    { label: 'AUC', value: modelInfo?.auc ?? '0.8943' },
                                    { label: 'Accuracy', value: '83.8%' },
                                ].map((m) => (
                                    <Grid item xs={6} sm={3} key={m.label}>
                                        <Box style={{ background: PALETTE.statBg, borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                                            <Typography variant="caption" style={{ color: PALETTE.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                {m.label}
                                            </Typography>
                                            <Typography variant="h6" style={{ fontWeight: 700, color: PALETTE.h2 }}>
                                                {m.value}
                                            </Typography>
                                            {m.caption && (
                                                <Typography variant="caption" style={{ color: PALETTE.textMuted }}>
                                                    {m.caption}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>

                            <Accordion
                                style={{
                                    marginTop: 16,
                                    borderRadius: 8,
                                    boxShadow: 'none',
                                    backgroundColor: PALETTE.infoBg,
                                    '&:before': { display: 'none' },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon style={{ color: PALETTE.h2 }} />}
                                    style={{ padding: '0 16px', minHeight: 44 }}
                                >
                                    <Typography style={{ fontWeight: 600, color: PALETTE.h2, fontSize: 14 }}>
                                        What do these metrics mean?
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails style={{ padding: '0 16px 16px', display: 'block' }}>
                                    <Typography variant="body2" style={{ color: PALETTE.textSecondary, marginBottom: 12 }}>
                                        These numbers describe how well the model performed when evaluated on the held-out validation
                                        cohort. They are population-level performance characteristics, not certainty about any single
                                        infant&apos;s result.
                                    </Typography>

                                    <Typography variant="subtitle2" style={{ color: PALETTE.text, marginTop: 8, marginBottom: 4 }}>
                                        Reference cohort
                                    </Typography>
                                    <Typography variant="body2" style={{ color: PALETTE.textSecondary, marginBottom: 12 }}>
                                        Z-scores are computed against <strong>n=127 typical-development infants</strong>. This is the
                                        baseline distribution: a z of 0 means &ldquo;exactly average for a typical infant in this cohort.&rdquo;
                                        The cohort is also the basis for the percentile ranking shown in the SUMMARY card.
                                    </Typography>

                                    <Typography variant="subtitle2" style={{ color: PALETTE.text, marginTop: 8, marginBottom: 4 }}>
                                        Sensitivity (Se) &mdash; catching at-risk infants
                                    </Typography>
                                    <Typography variant="body2" style={{ color: PALETTE.textSecondary, marginBottom: 12 }}>
                                        Of the infants who truly are at-risk, what fraction does the screen flag? <strong>92.1%</strong>
                                        means about 92 of every 100 at-risk infants are correctly identified; the remaining ~8 are
                                        missed (false negatives). Cribsy is deliberately tuned for high sensitivity.
                                    </Typography>

                                    <Typography variant="subtitle2" style={{ color: PALETTE.text, marginTop: 8, marginBottom: 4 }}>
                                        Specificity (Sp) &mdash; correctly clearing typical infants
                                    </Typography>
                                    <Typography variant="body2" style={{ color: PALETTE.textSecondary, marginBottom: 12 }}>
                                        Of the infants who truly are typical, what fraction does the screen correctly classify as
                                        typical? <strong>78.0%</strong> means about 22 of every 100 typical infants get a positive
                                        screen they don&apos;t need (false positives). This is the trade-off for prioritizing sensitivity:
                                        we&apos;d rather over-refer for clinical follow-up than miss an at-risk infant.
                                    </Typography>

                                    <Typography variant="subtitle2" style={{ color: PALETTE.text, marginTop: 8, marginBottom: 4 }}>
                                        PPV / NPV &mdash; what a result means in practice
                                    </Typography>
                                    <Typography variant="body2" style={{ color: PALETTE.textSecondary, marginBottom: 12 }}>
                                        <strong>PPV (Positive Predictive Value):</strong> if the screen flags an infant, what&apos;s the
                                        probability they truly are at-risk? <strong>NPV (Negative Predictive Value):</strong> if the
                                        screen clears an infant, what&apos;s the probability they truly are typical? PPV/NPV depend on
                                        the underlying prevalence in the population being screened, so the values shown
                                        (74.6% / 93.4%) reflect this validation sample and may differ in other settings.
                                    </Typography>

                                    <Typography variant="subtitle2" style={{ color: PALETTE.text, marginTop: 8, marginBottom: 4 }}>
                                        Likelihood ratios (LR+ / LR&minus;)
                                    </Typography>
                                    <Typography variant="body2" style={{ color: PALETTE.textSecondary, marginBottom: 12 }}>
                                        How much a result shifts the odds of being at-risk, independent of prevalence.
                                        <strong> LR+ = Se / (1&minus;Sp)</strong>: a positive screen multiplies the pre-test odds of
                                        being at-risk by ~4.2&times;. <strong>LR&minus; = (1&minus;Se) / Sp</strong>: a negative screen
                                        multiplies the pre-test odds by ~0.10&times; (i.e. roughly a 10&times; reduction). Useful when
                                        applying the screen to populations with different baseline rates.
                                    </Typography>

                                    <Typography variant="subtitle2" style={{ color: PALETTE.text, marginTop: 8, marginBottom: 4 }}>
                                        AUC &mdash; how well the score separates the two groups
                                    </Typography>
                                    <Typography variant="body2" style={{ color: PALETTE.textSecondary, marginBottom: 12 }}>
                                        Area Under the ROC Curve. Probability that, given one randomly chosen at-risk infant and one
                                        randomly chosen typical infant, the model assigns a more atypical score to the at-risk one.
                                        0.5 = chance, 1.0 = perfect; <strong>~0.89</strong> indicates strong discrimination.
                                    </Typography>

                                    <Typography variant="subtitle2" style={{ color: PALETTE.text, marginTop: 8, marginBottom: 4 }}>
                                        Accuracy
                                    </Typography>
                                    <Typography variant="body2" style={{ color: PALETTE.textSecondary, marginBottom: 4 }}>
                                        Fraction of all infants classified correctly (typical and at-risk combined). Less informative
                                        than Se/Sp on its own because it is sensitive to class balance &mdash; AUC and the Se/Sp pair
                                        give a more complete picture.
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        </CardContent>
                    </Card>

                    {/* Clinical Interpretation Guide */}
                    <Card style={cardSx}>
                        <CardContent>
                            <Typography style={sectionTitleSx}>CLINICAL INTERPRETATION GUIDE</Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" style={{ marginBottom: 8, color: PALETTE.text }}>
                                        <strong>Z-Score Interpretation:</strong>
                                    </Typography>
                                    <ul style={{ margin: 0, paddingLeft: 20, color: PALETTE.textSecondary }}>
                                        <li>Z {"<"} -0.25: Elevated risk, further evaluation recommended</li>
                                        <li>-0.25 ≤ Z {"<"} 0.5: Borderline, consider follow-up</li>
                                        <li>Z ≥ 0.5: Typical development likely</li>
                                    </ul>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" style={{ marginBottom: 8, color: PALETTE.text }}>
                                        <strong>Percentile Interpretation:</strong>
                                    </Typography>
                                    <ul style={{ margin: 0, paddingLeft: 20, color: PALETTE.textSecondary }}>
                                        <li>{"<"}25th: Less common movement patterns</li>
                                        <li>25-75th: Typical range</li>
                                        <li>{">"}75th: More typical than average</li>
                                    </ul>
                                </Grid>
                            </Grid>

                            <Box style={{ marginTop: 16, padding: 12, backgroundColor: PALETTE.infoBg, borderRadius: 6 }}>
                                <Typography variant="body2" style={{ color: PALETTE.info }}>
                                    <strong>Important:</strong> This is a screening tool, not a diagnostic instrument.
                                    Results should be interpreted in the context of clinical examination and developmental history.
                                    The model prioritizes sensitivity (92.1%) over specificity (78.0%) to minimize false negatives.
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>

                </Box>

            </DialogContent>
        </Dialog>
    );
}
